import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { MealPlanResponse } from '@meal-planner/shared';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { Icon } from '../components/ui/Icon.js';
import { GeneratePlanFlow } from '../components/GeneratePlanFlow.js';
import { useProfile } from '../hooks/useAuth.js';

interface FavoriteRecipe {
  id: string;
  recipeId: string;
  recipeName: string;
  mealType: string;
  createdAt: string;
}

function getNextMonday(): string {
  const d = new Date();
  const day = d.getDay();
  const diff = day === 0 ? 1 : 8 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().split('T')[0];
}

const MEAL_ACCENT: Record<string, string> = {
  breakfast: 'accent-saffron',
  lunch: 'accent-cobalt',
  dinner: 'accent-plum',
};

const MEAL_ORDER: Record<string, number> = { breakfast: 0, lunch: 1, dinner: 2 };

export function FavoritesPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: favorites = [], isLoading } = useQuery<FavoriteRecipe[]>({
    queryKey: ['favorites'],
    queryFn: () => api.get('/api/favorites'),
    staleTime: 5 * 60_000,
  });

  const { data: profile } = useProfile();

  const unfavorite = useMutation({
    mutationFn: (recipeId: string) => api.delete(`/api/favorites/${recipeId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['favorites'] }),
  });

  const [showGenFlow, setShowGenFlow] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const generate = useMutation<{ planId: string; plan: MealPlanResponse }>({
    mutationFn: () => api.post('/api/meal-plans/generate', { weekStart: getNextMonday() }),
    onSuccess: () => {
      setGenerateError(null);
      qc.invalidateQueries({ queryKey: ['meal-plans'] });
      qc.invalidateQueries({ queryKey: ['stats'] });
    },
    onError: (err: any) => {
      setGenerateError(err?.body?.error ?? 'Failed to generate plan. Try again.');
    },
  });
  const startGenerate = () => {
    setGenerateError(null);
    setShowGenFlow(true);
    generate.reset();
    generate.mutate();
  };
  const flowState: 'running' | 'success' | 'error' = generate.isPending
    ? 'running'
    : generate.isError
    ? 'error'
    : generate.isSuccess
    ? 'success'
    : 'running';

  const sorted = useMemo(() =>
    [...favorites].sort((a, b) => {
      const ord = (MEAL_ORDER[a.mealType] ?? 9) - (MEAL_ORDER[b.mealType] ?? 9);
      if (ord !== 0) return ord;
      return a.recipeName.localeCompare(b.recipeName);
    }),
  [favorites]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const f of favorites) c[f.mealType] = (c[f.mealType] ?? 0) + 1;
    return c;
  }, [favorites]);

  return (
    <Layout>
      <div className="w-full mx-auto" style={{ maxWidth: 1280, padding: '32px 24px 56px' }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="mb-8">
          <div className="text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
            <Icon name="heart" size={12} style={{ display: 'inline', marginRight: 6, marginBottom: -1 }} />
            Saved
          </div>
          <h1 className="h-display" style={{ fontSize: 'clamp(36px, 6vw, 56px)', margin: 0, color: 'var(--ink)', lineHeight: 1.0 }}>
            Your{' '}
            <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>favorites.</span>
          </h1>
          <p className="text-sm mt-3" style={{ color: 'var(--ink-3)' }}>
            {favorites.length === 0
              ? 'Heart any recipe to keep it close. They surface first in future plans.'
              : <>
                  <strong style={{ color: 'var(--ink)' }}>{favorites.length}</strong>{' '}
                  recipe{favorites.length === 1 ? '' : 's'} saved
                  {Object.keys(counts).length > 1 && (
                    <> · {(['breakfast', 'lunch', 'dinner'] as const)
                          .filter((m) => counts[m])
                          .map((m) => `${counts[m]} ${m}`)
                          .join(', ')}</>
                  )}
                </>
            }
          </p>
        </div>

        {/* ── Generate-with-favorites CTA ────────────────────────────── */}
        {favorites.length >= 1 && (
          <div className="card relative overflow-hidden p-5 mb-6 flex items-center justify-between gap-4 flex-wrap">
            <div className="absolute -top-10 -right-10 w-40 h-40 pointer-events-none"
              style={{ background: 'radial-gradient(circle, var(--accent-glow) 0%, transparent 70%)' }} />
            <div className="relative">
              <div className="text-[11px] font-semibold uppercase tracking-wider mb-1"
                style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
                Build a plan
              </div>
              <p className="h-serif" style={{ fontSize: 17, color: 'var(--ink)', margin: 0, lineHeight: 1.3 }}>
                Generate next week with your favorites baked in.
              </p>
              <p className="text-xs mt-1" style={{ color: 'var(--ink-3)' }}>
                These recipes are auto-boosted in every plan you generate.
              </p>
            </div>
            <button className="btn btn-primary relative"
              onClick={startGenerate}
              disabled={generate.isPending}
              style={{ padding: '12px 18px', fontSize: 13.5, opacity: generate.isPending ? 0.6 : 1 }}>
              <Icon name="sparkle" size={14} />
              Generate with favorites
              <Icon name="arrow" size={13} />
            </button>
            {generateError && (
              <p className="text-xs w-full relative" style={{ color: 'var(--danger)' }}>{generateError}</p>
            )}
          </div>
        )}

        {/* ── Recipe rows ────────────────────────────────────────────── */}
        {isLoading ? (
          <div className="space-y-2.5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse rounded-xl h-16" style={{ background: 'var(--surface-2)' }} />
            ))}
          </div>
        ) : favorites.length === 0 ? (
          <div className="card p-12 text-center" style={{ maxWidth: 560, margin: '0 auto' }}>
            <div className="inline-flex items-center justify-center mb-4 rounded-full"
              style={{ width: 56, height: 56, background: 'var(--accent-soft)', color: 'var(--accent)' }}>
              <Icon name="heart" size={24} />
            </div>
            <p className="h-serif" style={{ fontSize: 18, color: 'var(--ink)', margin: 0, marginBottom: 4 }}>
              No favorites yet
            </p>
            <p className="text-sm mb-5" style={{ color: 'var(--ink-3)' }}>
              Tap the heart on any recipe to save it here.
            </p>
            <Link to="/meal-plans" className="btn btn-soft"
              style={{ padding: '10px 18px', fontSize: 13.5 }}>
              Browse meal plans
              <Icon name="arrow" size={14} />
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {sorted.map((fav) => {
              const accentClass = MEAL_ACCENT[fav.mealType] ?? '';
              return (
                <div key={fav.id}
                  className={`card ${accentClass} flex items-center justify-between gap-4 px-5 py-4`}>
                  <Link to={`/recipes/${fav.recipeId}`}
                    className="flex items-center gap-3 min-w-0 flex-1 group">
                    <span className="flex-shrink-0 inline-flex items-center justify-center rounded-lg"
                      style={{
                        width: 40, height: 40,
                        background: 'var(--accent-soft)',
                        color: 'var(--accent)',
                      }}>
                      <Icon name="plate" size={18} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[11px] font-semibold uppercase tracking-wider mb-0.5"
                        style={{ color: 'var(--accent)', letterSpacing: '0.12em' }}>
                        {fav.mealType}
                      </div>
                      <div className="h-serif truncate transition-colors"
                        style={{ fontSize: 15.5, color: 'var(--ink)', lineHeight: 1.2 }}>
                        {fav.recipeName}
                      </div>
                    </div>
                  </Link>
                  <button
                    onClick={() => unfavorite.mutate(fav.recipeId)}
                    disabled={unfavorite.isPending}
                    aria-label={`Remove ${fav.recipeName} from favorites`}
                    className="flex-shrink-0 inline-flex items-center justify-center transition-colors"
                    style={{
                      width: 36, height: 36, borderRadius: 10,
                      color: 'var(--danger)',
                      background: 'transparent',
                      opacity: unfavorite.isPending ? 0.5 : 1,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'color-mix(in oklab, var(--danger) 14%, transparent)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                  >
                    <Icon name="heart" size={18} stroke={2.2} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showGenFlow && (
        <GeneratePlanFlow
          state={flowState}
          errorMessage={generateError}
          blwActive={profile?.familyMembers?.some((m) => m.age === 0) ?? false}
          hiddenVeggieMode={profile?.hiddenVeggieMode ?? false}
          pickyEaterMode={profile?.pickyEaterMode ?? false}
          onReview={() => {
            const id = generate.data?.planId;
            setShowGenFlow(false);
            if (id) navigate(`/meal-plans/${id}`);
            else navigate('/meal-plans');
          }}
          onReroll={() => {
            generate.reset();
            generate.mutate();
          }}
          onClose={() => {
            setShowGenFlow(false);
            generate.reset();
          }}
        />
      )}
    </Layout>
  );
}
